import { useEffect, useState } from 'react'

import { Separator } from 'components'
import { ActionIconEnum } from 'enums/ActionIcon'
import { PageTitles } from 'enums/PageTitles'
import { useNavBar } from 'hooks/useNavBar'
import useBoundState from 'store'
import { IClinic } from 'store/coveredClinics/types'
import BridgeService from 'services/bridgeService'

import { uuid } from '@interco/inter-ui'
import { SelectBottomSheet } from '@interco/inter-ui/components/BottomSheet/SelectBottomSheet'
import { Option } from '@interco/inter-ui/components/BottomSheet/SelectBottomSheet/types'
import { Text } from '@interco/inter-ui/components/Text'

import * as S from './PartnerClinics.styles'
import { ShimmerClinics } from './ShimmerClinics/ShimmerClinics'

const formatClinicAddress = (address: IClinic['address']) => {
  const streetLine = [address.street, address.number].filter(Boolean).join(', ')

  if (address.neighborhood) {
    return streetLine ? `${streetLine} - ${address.neighborhood}` : address.neighborhood
  }

  return streetLine
}

export const PartnerClinics = () => {
  const isLoading = useBoundState((state) => state.isLoading)
  const partnerClinics = useBoundState((state) => state.clinics)
  const locationsList = useBoundState((state) => state.locations)
  const currentLocation = useBoundState((state) => state.currentLocation)

  const setCurrentLocation = useBoundState((state) => state.setCurrentLocation)
  const getCoveredClinics = useBoundState((state) => state.getCoveredClinics)

  const [currentCity, setCurrentCity] = useState<Option | undefined>(undefined)
  const [locationOptions, setLocationOptions] = useState<Option[]>([])

  useNavBar({
    navbarTitle: PageTitles.CLINICS,
    actionIcons: {
      leftIcon: ActionIconEnum.BACK,
    },
  })

  useEffect(() => {
    setCurrentCity({
      text: currentLocation.city,
      value: currentLocation.ibgeCode,
    })

    if (locationsList) {
      const options = locationsList.map((location) => ({
        text: location.city,
        value: location.ibgeCode,
      }))

      setLocationOptions(options)
    }
  }, [])

  const handleSelectCity = (value: Option) => {
    const ibgeCode = `${value.value}`

    getCoveredClinics({
      ibgeCode,
    })

    setCurrentCity(value)

    setCurrentLocation({
      city: value.text,
      ibgeCode,
    })
  }

  useEffect(() => {
    BridgeService.tacWb({
      name: 'screen_partner_clinics_list',
      module: 'pet',
      actions: ['open_partner_clinics_via_home', 'open_partner_clinics_via_after_sales'],
      params: {
        currentCity: JSON.stringify(currentCity),
        partnerClinics: JSON.stringify(partnerClinics),
        locationsList: JSON.stringify(locationsList),
        locationOptions: JSON.stringify(locationOptions),
        currentLocation: JSON.stringify(currentLocation),
        setCurrentLocation: JSON.stringify(setCurrentLocation),
        getCoveredClinics: JSON.stringify(getCoveredClinics),
      },
    })
  }, [])

  const partnerClinicsList = partnerClinics.map((clinic, index) => (
    <S.Clinics key={uuid()}>
      <Text variant="body-3" bold colorWeight={500}>
        {clinic.name}
      </Text>
      <Text variant="caption-1" bold color="primary" colorWeight={500}>
        {clinic.phone}
      </Text>
      <Text variant="caption-1" as="p" bold>
        {clinic.type === 'Domicilio' || clinic.type === 'Volante' ? (
          <>
            (Atendimento domiciliar)
            <br />
            {formatClinicAddress(clinic.address)}
          </>
        ) : (
          formatClinicAddress(clinic.address)
        )}
      </Text>

      {index !== partnerClinics.length - 1 && (
        <Separator borderWidth="1px" type="dashed" color="var(--gray200)" margin="16px 0" />
      )}
    </S.Clinics>
  ))

  const renderPartnerList =
    partnerClinics.length > 0 ? (
      partnerClinicsList
    ) : (
      <Text variant="body-3">
        No momento não há clínicas credenciadas nessa cidade, tente outra cidade
      </Text>
    )

  return (
    <S.Container>
      <Text variant="headline-h3" as="h3" semiBold>
        Encontre as clínicas parceiras mais perto de você
      </Text>

      <S.CoveredCitiesBS>
        <SelectBottomSheet
          id="select"
          placeholder="Selecione a cidade"
          isUsingFilter
          fixedHeight="60vh"
          placeholderFilter="Busque por..."
          options={locationOptions}
          onChange={handleSelectCity}
          value={currentCity}
          isLoading={isLoading}
        />
      </S.CoveredCitiesBS>

      {isLoading ? <ShimmerClinics /> : renderPartnerList}
    </S.Container>
  )
}
